/**
 * Runtime configuration — values injected at container startup, not build time.
 *
 * In production the Nginx entrypoint generates /config.js from environment
 * variables, which sets window.__RUNTIME_CONFIG__. In local dev (Vite),
 * the VITE_* env vars are used as fallbacks.
 *
 * This allows the same Docker image to be deployed to multiple environments
 * (staging, production) with different Entra ID tenants/clients without
 * rebuilding.
 */

interface RuntimeConfig {
  ENTRA_CLIENT_ID: string;
  ENTRA_TENANT_ID: string;
  ENTRA_REDIRECT_URI: string;
  ENTRA_API_SCOPE: string;
  API_URL: string;
}

declare global {
  interface Window {
    __RUNTIME_CONFIG__?: Partial<RuntimeConfig>;
  }
}

function get(key: keyof RuntimeConfig, viteKey: string, fallback: string = ''): string {
  // 1. Runtime config (injected by Nginx at container start)
  const runtimeValue = window.__RUNTIME_CONFIG__?.[key];
  if (runtimeValue) return runtimeValue;

  // 2. Vite build-time env vars (for local dev)
  const viteValue = (import.meta.env as Record<string, string | undefined>)[viteKey];
  if (viteValue) return viteValue;

  // 3. Fallback
  return fallback;
}

export const runtimeConfig: RuntimeConfig = {
  ENTRA_CLIENT_ID: get('ENTRA_CLIENT_ID', 'VITE_ENTRA_CLIENT_ID'),
  ENTRA_TENANT_ID: get('ENTRA_TENANT_ID', 'VITE_ENTRA_TENANT_ID'),
  ENTRA_REDIRECT_URI: get('ENTRA_REDIRECT_URI', 'VITE_ENTRA_REDIRECT_URI', window.location.origin),
  ENTRA_API_SCOPE: get('ENTRA_API_SCOPE', 'VITE_ENTRA_API_SCOPE'),
  API_URL: get('API_URL', 'VITE_API_URL', '/api/v1'),
};
